import { useState, useEffect } from "react";
import { X, Clock, Calendar, Loader2, AlertCircle, Plus, Edit } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import Swal from "sweetalert2";
import { 
  obtenerTurnosZonaComun,
  TurnoZonaComun 
} from "../../../../api/zonaComunTurnosService";
import ManageTurnoModal from "./ManageTurnoModal";

interface TurnosPorDiaModalProps {
  isOpen: boolean;
  horarioPorDiaCodigo: number;
  zonaComunCodigo: string;
  nombreDia: string;
  horaApertura: string;
  horaCierre: string;
  onClose: () => void;
}

export default function TurnosPorDiaModal({ 
  isOpen, 
  horarioPorDiaCodigo,
  zonaComunCodigo,
  nombreDia,
  horaApertura,
  horaCierre,
  onClose 
}: TurnosPorDiaModalProps) {
  const [turnos, setTurnos] = useState<TurnoZonaComun[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showManageModal, setShowManageModal] = useState(false);
  const [selectedTurno, setSelectedTurno] = useState<TurnoZonaComun | null>(null);

  useEffect(() => {
    if (isOpen && horarioPorDiaCodigo) {
      loadTurnos();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, horarioPorDiaCodigo]);

  const loadTurnos = async () => {
    if (!horarioPorDiaCodigo) return;

    try {
      setIsLoading(true);
      setError(null);

      const payload = {
        codigo: "",
        "zona comun horario por dia codigo": horarioPorDiaCodigo,
        "estatus codigo": "",
      };

      const response = await obtenerTurnosZonaComun(payload);

      if (response.code === "1" && response.object_) {
        setTurnos(response.object_);
      } else {
        setTurnos([]);
        setError(response.message || "No se encontraron turnos");
      }
    } catch (error) {
      console.error("Error al cargar turnos:", error);
      setError("Error al cargar los turnos");
      setTurnos([]);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudieron cargar los turnos",
        confirmButtonColor: "#d33",
        customClass: {
          popup: 'z-[10002]',
          container: 'z-[10002]'
        },
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10002';
          const container = popup.closest('.swal2-container');
          if (container) {
            (container as HTMLElement).style.zIndex = '10002';
          }
        },
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedTurno(null);
    setShowManageModal(true);
  };

  const handleEdit = (turno: TurnoZonaComun) => {
    setSelectedTurno(turno);
    setShowManageModal(true);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10001] p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="sticky top-0 bg-gradient-to-r from-military-500 to-military-600 text-white px-6 py-4 rounded-t-xl flex items-center justify-between z-10">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Calendar size={28} />
            Turnos por Día - {nombreDia}
          </h2>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Banner informativo sutil */}
          <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calendar className="text-gray-500" size={18} />
                <span className="text-sm font-semibold text-gray-700">{nombreDia}</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-gray-500" />
                  <span className="text-gray-600">Apertura:</span>
                  <span className="font-medium text-gray-800">{horaApertura}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock size={14} className="text-gray-500" />
                  <span className="text-gray-600">Cierre:</span>
                  <span className="font-medium text-gray-800">{horaCierre}</span>
                </div>
              </div>
            </div>
          </div>

          {isLoading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Loader2 className="h-12 w-12 animate-spin text-military-600 mb-4" />
              <p className="text-gray-600">Cargando turnos...</p>
            </div>
          ) : error && turnos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 text-center">{error}</p>
            </div>
          ) : turnos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Clock className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 text-center">
                No hay turnos configurados para este día.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {turnos.map((turno, index) => {
                    const codigoTurno = turno.codigo || turno.ztr_codigo;
                    const inicio = turno["inicio turno"] || turno.ztr_inicio_turno;
                    const fin = turno["fin turno"] || turno.ztr_fin_turno;
                    const estatus = turno.ztr_estatus; // Or mapped key if added
                    
                    return (
                      <div
                        key={codigoTurno || index}
                        onClick={() => handleEdit(turno)}
                        className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-5 border-2 border-gray-200 shadow-lg hover:shadow-xl transition-all cursor-pointer group relative"
                      >
                         <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="bg-gray-100 p-1.5 rounded-full text-gray-600 hover:bg-gray-200">
                                <Edit size={16} />
                            </div>
                         </div>

                        <div className="flex items-center gap-3 mb-4">
                          <div className="bg-gradient-to-br from-military-500 to-military-600 text-white rounded-lg p-3 shadow-lg">
                            <Clock size={20} />
                          </div>
                          <div>
                            <h3 className="font-bold text-lg text-gray-900">
                              Turno {index + 1}
                            </h3>
                            {codigoTurno && (
                              <p className="text-xs text-gray-500">
                                Código: {codigoTurno}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-3 border border-blue-200">
                            <div className="bg-blue-500 text-white rounded-lg p-2">
                              <Clock size={16} />
                            </div>
                            <div className="flex-1">
                              <span className="text-xs font-semibold text-gray-600 uppercase">Inicio</span>
                              <p className="text-base font-bold text-blue-700">
                                {inicio || "N/A"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-3 border border-purple-200">
                            <div className="bg-purple-500 text-white rounded-lg p-2">
                              <Clock size={16} />
                            </div>
                            <div className="flex-1">
                              <span className="text-xs font-semibold text-gray-600 uppercase">Fin</span>
                              <p className="text-base font-bold text-purple-700">
                                {fin || "N/A"}
                              </p>
                            </div>
                          </div>
                          {turno.ztr_estatus_nombre && (
                            <div className="mt-2">
                              <span className={`inline-block px-3 py-1.5 rounded-full text-xs font-bold ${
                                estatus === "A" || turno.ztr_estatus?.toLowerCase().includes("activo")
                                  ? "bg-green-100 text-green-800 border-2 border-green-300" 
                                  : estatus === "I" || turno.ztr_estatus?.toLowerCase().includes("inactivo")
                                  ? "bg-red-100 text-red-800 border-2 border-red-300"
                                  : "bg-gray-100 text-gray-800 border-2 border-gray-300"
                              }`}>
                                {turno.ztr_estatus_nombre}
                              </span>
                            </div>
                          )}
                          {turno.estado_turno_nombre && (
                            <div className="mt-2">
                              <span className="text-xs text-gray-600 font-semibold">Estado:</span>
                              <span className={`ml-2 inline-block px-3 py-1.5 rounded-full text-xs font-bold ${
                                turno.estado_turno?.toLowerCase().includes("disponible")
                                  ? "bg-blue-100 text-blue-800 border-2 border-blue-300" 
                                  : turno.estado_turno?.toLowerCase().includes("reservado")
                                  ? "bg-orange-100 text-orange-800 border-2 border-orange-300"
                                  : "bg-gray-100 text-gray-800 border-2 border-gray-300"
                              }`}>
                                {turno.estado_turno_nombre}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t pt-4 px-6 pb-6">
          <div className="flex justify-between items-center">
            <Button
              onClick={handleCreate}
              className="bg-military-500 hover:bg-military-600 text-white shadow-lg font-semibold flex items-center gap-2"
            >
              <Plus size={18} />
              Agregar Turno por Día
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="border-2 hover:bg-gray-50"
            >
              Cerrar
            </Button>
          </div>
        </div>
      </div>

      <ManageTurnoModal
        isOpen={showManageModal}
        diaCodigo={horarioPorDiaCodigo}
        turnoToEdit={selectedTurno}
        onClose={(saved) => {
          setShowManageModal(false);
          if (saved) {
            loadTurnos();
          }
        }}
      />
    </div>
  );
}

